import { useState, type FormEvent } from 'react'
import {
  AleloButton,
  AleloError,
  AleloLayout,
  AleloPasswordInput,
} from '@/components/AleloLayout'
import { AleloCaptcha } from '@/components/AleloCaptcha'
import { AleloStepper, stepperTotal } from '@/components/AleloStepper'
import styles from '@/components/AleloLayout.module.css'
import { SignupOtpStep } from '@/screens/signup-shared/SignupOtpStep'
import { mapClosingError } from '@/screens/signup-shared/otpErrors'
import { useSignupVerification } from '@/screens/signup-shared/useSignupVerification'
import { useSignupPasswordManager } from './hooks/useSignupPasswordManager'
import locales from './locales/pt-BR.json'
import { errorCopy } from '@/utils/transactionError'

/**
 * Última etapa da jornada: Dados → E-mail → Celular (as três do wizard em
 * `signup-id`) → Criar senha. O total ignora a config do BFF pelo mesmo
 * motivo do wizard — as telas existem sempre, os canais só decidem se cada
 * uma pede código.
 */
const TOTAL_STEPS = stepperTotal(undefined, true)

/**
 * Identifier First — senha após signup-id. É o `signup()` desta tela que cria
 * a conta, então as sub-etapas de confirmação (`[otp-email] → [otp-sms]`)
 * vêm entre a senha e esse `signup()`; o token de verificação vai no mesmo
 * payload. O indicador de etapas só aparece quando algum canal é exigido.
 */
export default function SignupPasswordScreen() {
  const {
    startVerification,
    completeSignup,
    handleEditIdentifier,
    handleBack,
    handleBackToLogin,
    goToLogin,
    userExists,
    displayId,
    destinations,
    captchaConfig,
    captchaReady,
    onCaptchaToken,
    error: sdkError,
  } = useSignupPasswordManager()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const verification = useSignupVerification<string>({
    // Celular e e-mail já passaram pelo passo 3: erro de identidade aqui é
    // dado perdido entre as telas, e a copy manda voltar.
    start: async (pwd, options) => {
      const result = await startVerification(pwd, options)
      return result.ok ? result : { ok: false, error: mapClosingError(result.error) }
    },
    complete: (pwd, token) => completeSignup(pwd, token),
    describeError,
    failed: locales.failed,
  })
  const { channels, submitted, currentChannel, error, setError, pending } =
    verification

  function describeError(err: unknown): string {
    if (
      err instanceof Error &&
      (err.message === 'PASSWORD_WEAK' || err.message === 'PASSWORD_POLICY')
    ) {
      return locales.weakPassword
    }
    if (err instanceof Error && err.message === 'CAPTCHA_REQUIRED') {
      return locales.captchaRequired
    }
    return locales.failed
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    if (password.length < 8) {
      setError(locales.weakPassword)
      return
    }
    if (password !== confirm) {
      setError(locales.passwordMismatch)
      return
    }
    await verification.begin(password)
  }

  // Rota de contingência: o wizard de `signup-id` não deixou token (aba nova,
  // sessionStorage perdido) e a confirmação precisa acontecer aqui. Numeração
  // por canal, porque nesse caminho o OTP vem depois da senha.
  if (verification.step === 'otp' && submitted !== null && channels && currentChannel) {
    return (
      <SignupOtpStep
        channel={currentChannel}
        channels={channels}
        destination={destinations[currentChannel]}
        currentStep={verification.currentStep}
        totalSteps={verification.totalSteps}
        code={verification.code}
        onCodeChange={verification.setCode}
        error={error || errorCopy(sdkError, locales.errors)}
        pending={pending}
        onSubmit={(event) => void verification.confirm(event)}
        resendSeconds={verification.resendSeconds}
        onResend={() => void verification.resend()}
      />
    )
  }

  return (
    <AleloLayout>
      <AleloStepper currentStep={TOTAL_STEPS} withPassword />
      <p className={styles.signupHint}>{`Passo ${TOTAL_STEPS} de ${TOTAL_STEPS}`}</p>
      <h1 className={styles.title}>{locales.title}</h1>
      <p className={styles.subtitle}>{locales.description}</p>
      {displayId ? (
        <div className={styles.identifierRow}>
          <p className={styles.identifierValue}>{displayId}</p>
          <AleloButton variant="link" onClick={handleEditIdentifier}>
            {locales.editIdentifier}
          </AleloButton>
        </div>
      ) : null}
      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        <AleloPasswordInput
          id="password"
          name="password"
          autoComplete="new-password"
          label={locales.passwordLabel}
          placeholder={locales.passwordPlaceholder}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
        />
        <AleloPasswordInput
          id="confirm"
          autoComplete="new-password"
          label={locales.confirmPasswordLabel}
          placeholder={locales.confirmPasswordPlaceholder}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
        />
        <AleloCaptcha config={captchaConfig} onToken={onCaptchaToken} />
        <AleloError
          message={userExists ? locales.userAlreadyRegistered : error || errorCopy(sdkError, locales.errors)}
        />
        <AleloButton type="submit" disabled={pending || !captchaReady}>
          {locales.submit}
        </AleloButton>
        <div className={styles.linkRow}>
          <AleloButton variant="link" onClick={handleBack}>
            {locales.back}
          </AleloButton>
          {/* Conta já existente: o mesmo link leva o CPF pro login. */}
          <AleloButton
            variant="link"
            onClick={userExists ? goToLogin : handleBackToLogin}
          >
            {locales.backToLogin}
          </AleloButton>
        </div>
      </form>
    </AleloLayout>
  )
}
